import { ChangeEvent, FormEvent, memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  TextField,
} from '@mui/material';

import { Strong } from '@/components/rich-text-formatting';
import SmsBackupOptions from '@/legacy/components/user/twoFactorAuthentication/shared/SmsBackupOptionsFragment';
import { TwoFactorAuthenticationType } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';

function getCodeLabel(twoFactorAuthentication: TwoFactorAuthenticationType): string {
  switch (twoFactorAuthentication) {
    case TwoFactorAuthenticationType.Sms:
      return 'Enter SMS Code';

    case TwoFactorAuthenticationType.Otp:
      return 'Enter Authenticator Code';

    default:
      return null;
  }
}

interface Props {
  code: string;
  errorMessage: string;
  isLoading: boolean;
  isOpen: boolean;
  onCodeUpdate: (code: string) => void;
  onSubmitCode: () => void;
  onSwitchToMultipleMode: () => void;
  onClose: () => void;
  onResendCode: () => void;
  onCallMe: () => void;
  twoFactorDisplayName: string;
  twoFactorAuthenticationType: TwoFactorAuthenticationType;
  numberOfTwoFactorAuthentications: number;
  rememberDevice: boolean;
  onRememberDeviceChange: (rememberDevice: boolean) => void;
}

const LoginPopupDefaultMethodDialog = (props: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const {
    code,
    errorMessage,
    isLoading = false,
    isOpen,
    onCodeUpdate,
    onResendCode,
    onCallMe,
    twoFactorDisplayName,
    twoFactorAuthenticationType,
    numberOfTwoFactorAuthentications,
    onSubmitCode,
    onSwitchToMultipleMode,
    onRememberDeviceChange,
    onClose,
    rememberDevice,
  } = props;

  const handleCodeSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSubmitCode();
    },
    [onSubmitCode]
  );

  const handleCodeChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onCodeUpdate(e.target.value);
    },
    [onCodeUpdate]
  );

  const handleRememberDeviceChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onRememberDeviceChange(e.target.checked),
    [onRememberDeviceChange]
  );

  return (
    <Dialog open={isOpen}>
      <form onSubmit={handleCodeSubmit}>
        <DialogTitle>
          <FormattedMessage defaultMessage="Two-Step Authentication" />
        </DialogTitle>
        {isLoading && (
          <DialogContent>
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          </DialogContent>
        )}
        {!isLoading && (
          <DialogContent>
            <DialogContentText gutterBottom>
              <FormattedMessage
                defaultMessage="Please enter the two-step verification code from <Strong>{twoFactorDisplayName}</Strong>:"
                values={{ twoFactorDisplayName, Strong }}
              />
            </DialogContentText>

            <FormControl variant="outlined" fullWidth margin="normal">
              <TextField
                name="two-factor-authentication-code"
                onChange={handleCodeChange}
                label={getCodeLabel(twoFactorAuthenticationType)}
                value={code}
                autoFocus
                required
                autoComplete="off"
                error={!!errorMessage}
                helperText={errorMessage}
                fullWidth
              />
            </FormControl>

            {twoFactorAuthenticationType === TwoFactorAuthenticationType.Sms && (
              <SmsBackupOptions onResendCode={onResendCode} onCallMe={onCallMe} />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  color="primary"
                  checked={rememberDevice}
                  disabled={isLoading}
                  onChange={handleRememberDeviceChange}
                />
              }
              label={formatMessage({
                defaultMessage: 'Remember this device for the next 30 days',
              })}
            />

            {numberOfTwoFactorAuthentications === 1 && (
              <DialogContentText>
                <FormattedMessage
                  defaultMessage="You can add additional two-step authentication methods by going to <Strong>My Profile > Two-Step Authentication</Strong>."
                  values={{ Strong }}
                />
              </DialogContentText>
            )}

            {numberOfTwoFactorAuthentications > 1 && (
              <>
                <DialogContentText gutterBottom>
                  <Button variant="text" onClick={onSwitchToMultipleMode} sx={{ ml: -1 }}>
                    <FormattedMessage defaultMessage="Use another two-step authentication method" />
                  </Button>
                </DialogContentText>
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="{twoFactorDisplayName} has been set as your default two-step authentication method. To update your default method, go to <Strong>My Profile > Two-Step Authentication</Strong>."
                    values={{
                      twoFactorDisplayName,
                      Strong,
                    }}
                  />
                </DialogContentText>
              </>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button variant="outlined" onClick={onClose} type="button">
            <FormattedMessage defaultMessage="Back" />
          </Button>
          <Button color="primary" type="submit" disabled={isLoading}>
            <FormattedMessage defaultMessage="Submit" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default memo(LoginPopupDefaultMethodDialog);
export type { Props };
